import React from "react";
import ReactDOM from "react-dom/client";
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Box,
    Fade,
    Grid,
    MenuItem,
    Autocomplete,
    FormControlLabel,
} from "@mui/material";
import { StyledEngineProvider } from "@mui/material/styles";
import ThemeProvider from "../../theme";
import Iconify from "../iconify";
import { enqueueSnackbar } from "notistack";
import axios from "axios";

const ReservationPromptDialog = ({ onResolve, onReject, options = {} }) => {
    const isTabsPos = !!options?.isTabsPos;

    const estimatedPreparationTimeOptions = React.useMemo(() => {
        const list = [];
        for (let t = 15; t <= 90; t += 5) list.push(t);
        return list;
    }, []);

    const reservedDesignatedNumbers = React.useMemo(() => {
        const raw = Array.isArray(options?.reservedDesignatedNumbers)
            ? options.reservedDesignatedNumbers
            : [];
        const nums = raw
            .map((v) => parseInt(v, 10))
            .filter((n) => Number.isFinite(n) && n >= 1 && n <= 200);
        return new Set(nums);
    }, [options?.reservedDesignatedNumbers]);

    const designatedNumberOptions = React.useMemo(() => {
        const list = [];
        for (let n = 1; n <= 200; n += 1) {
            if (!reservedDesignatedNumbers.has(n)) list.push(n);
        }
        return list;
    }, [reservedDesignatedNumbers]);

    const [formData, setFormData] = React.useState(() => {
        if (isTabsPos) {
            return {
                notes: "",
                estimated_preparation_time: 15,
                designated_number: "",
                use_delivery_provider: false,
                delivery_provider: "",
                customer: "",
                mobile_no: "",
                address_line1: "",
                address_line2: "",
            };
        }
        return {
            notes: "",
            adults: "",
            kids: "",
            babies: "",
        };
    });
    const [open, setOpen] = React.useState(true);

    const {
        title = "Reservation Details",
        message = "Please enter the reservation details:",
        confirmText = "Confirm",
        cancelText = "Cancel",
    } = options;

    const dialogTitle = React.useMemo(() => {
        if (!isTabsPos) return title;
        const hasDesignated = !!formData?.designated_number;
        return `${title} (${hasDesignated ? "dine-in" : "take-away"})`;
    }, [isTabsPos, title, formData?.designated_number]);

    const handleInputChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleNumberChange = (field, value) => {
        // Allow empty string, otherwise ensure it's a valid positive number
        if (value === "" || value === null || value === undefined) {
            handleInputChange(field, "");
        } else {
            const numValue = Math.max(0, parseInt(value) || 0);
            handleInputChange(field, numValue);
        }
    };

    const [deliveryProviders, setDeliveryProviders] = React.useState([]);
    const [deliveryProvidersLoading, setDeliveryProvidersLoading] = React.useState(false);
    const [deliveryProviderInputValue, setDeliveryProviderInputValue] = React.useState("");

    const fetchDeliveryProviders = React.useCallback(async () => {
        setDeliveryProvidersLoading(true);
        try {
            const params = {
                fields: JSON.stringify(["name", "company"]),
                limit_page_length: 0,
            };
            const { data } = await axios.get("resource/Delivery Providers", { params });
            const list = Array.isArray(data?.data) ? data.data : [];
            setDeliveryProviders(list);
            return list;
        } finally {
            setDeliveryProvidersLoading(false);
        }
    }, []);

    React.useEffect(() => {
        if (!isTabsPos) return;
        if (!formData.use_delivery_provider) return;
        fetchDeliveryProviders().then((list) => {
            if (!formData.delivery_provider && Array.isArray(list) && list.length > 0) {
                setFormData((prev) => ({
                    ...prev,
                    delivery_provider: list[0].name,
                }));
                setDeliveryProviderInputValue(list[0].company || list[0].name);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTabsPos, formData.use_delivery_provider]);

    const [customerOptions, setCustomerOptions] = React.useState([]);
    const [customerLoading, setCustomerLoading] = React.useState(false);
    const [customerInputValue, setCustomerInputValue] = React.useState("");

    const normalizeHtmlText = (html) => {
        if (!html) return "";
        try {
            return String(html).replace(/<[^>]+>/g, "").trim();
        } catch {
            return String(html);
        }
    };

    const parsePrimaryAddressLines = (primaryAddress) => {
        const raw = String(primaryAddress || "");
        if (!raw) return { line1: "", line2: "" };
        const parts = raw.split("<br>").map((p) => normalizeHtmlText(p)).filter(Boolean);
        return { line1: parts[0] || "", line2: parts[1] || "" };
    };

    const searchCustomers = React.useCallback(async (q) => {
        const query = String(q || "").trim();
        if (!query) {
            setCustomerOptions([]);
            return;
        }
        setCustomerLoading(true);
        try {
            const params = {
                fields: JSON.stringify(["name", "customer_name", "mobile_no", "primary_address"]),
                filters: JSON.stringify([["Customer", "customer_name", "like", `%${query}%`]]),
                limit_page_length: 20,
            };
            const { data } = await axios.get("resource/Customer", { params });
            const list = Array.isArray(data?.data) ? data.data : [];
            setCustomerOptions(list);
        } finally {
            setCustomerLoading(false);
        }
    }, []);

    React.useEffect(() => {
        if (!isTabsPos) return;
        if (!formData.use_delivery_provider) return;
        const t = setTimeout(() => searchCustomers(customerInputValue), 300);
        return () => clearTimeout(t);
    }, [isTabsPos, formData.use_delivery_provider, customerInputValue, searchCustomers]);

    const handleConfirm = () => {
        if (!isTabsPos) {
            if (formData.adults === "" || formData.adults <= 0) {
                enqueueSnackbar("Please enter the number of adults", {
                    variant: "error",
                });
                return;
            }
        }
        setOpen(false);

        if (isTabsPos) {
            if (formData.use_delivery_provider) {
                if (!formData.delivery_provider) {
                    enqueueSnackbar("Please select a delivery provider", {
                        variant: "error",
                    });
                    setOpen(true);
                    return;
                }
                if (!String(formData.customer || "").trim()) {
                    enqueueSnackbar("Please enter the customer", {
                        variant: "error",
                    });
                    setOpen(true);
                    return;
                }
                if (!String(formData.mobile_no || "").trim()) {
                    enqueueSnackbar("Please enter the mobile number", {
                        variant: "error",
                    });
                    setOpen(true);
                    return;
                }
                if (!String(formData.address_line1 || "").trim()) {
                    enqueueSnackbar("Please enter the address", {
                        variant: "error",
                    });
                    setOpen(true);
                    return;
                }
            }

            const prepTime = parseInt(formData.estimated_preparation_time, 10);
            const normalizedPrepTime = Number.isFinite(prepTime)
                ? prepTime
                : 15;

            const designated =
                formData.designated_number === "" ||
                formData.designated_number === null ||
                formData.designated_number === undefined
                    ? ""
                    : String(formData.designated_number);

            const finalData = {
                notes: formData.notes || "",
                estimated_preparation_time: normalizedPrepTime,
                designated_number: designated,
                use_delivery_provider: !!formData.use_delivery_provider,
                delivery_provider: formData.use_delivery_provider
                    ? formData.delivery_provider || ""
                    : "",
                customer: formData.customer || "",
                mobile_no: formData.mobile_no || "",
                address_line1: formData.address_line1 || "",
                address_line2: formData.address_line2 || "",
            };
            onResolve(finalData);
            return;
        }

        // Convert empty strings to appropriate defaults for the final result
        const finalData = {
            ...formData,
            adults: formData.adults === "" ? 1 : formData.adults,
            kids: formData.kids === "" ? 0 : formData.kids,
            babies: formData.babies === "" ? 0 : formData.babies,
        };
        onResolve(finalData);
    };

    const handleCancel = () => {
        setOpen(false);
        onResolve(null);
    };

    const handleKeyDown = (event) => {
        // Allow Ctrl+Enter or Cmd+Enter to submit
        if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
            event.preventDefault();
            event.stopPropagation();
            handleConfirm();
        }
    };

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider>
                <Dialog
                    open={open}
                    onClose={handleCancel}
                    maxWidth="sm"
                    fullWidth
                    TransitionComponent={Fade}
                    transitionDuration={{ enter: 400, exit: 300 }}
                    PaperProps={{
                        sx: (theme) => ({
                            boxShadow:
                                theme.customShadows?.dialog ||
                                theme.shadows[24],
                            overflow: "visible",
                        }),
                    }}
                    sx={{
                        "& .MuiBackdrop-root": {
                            backgroundColor: "rgba(0, 0, 0, 0.6)",
                            backdropFilter: "blur(4px)",
                        },
                    }}
                >
                    <DialogTitle
                        sx={(theme) => ({
                            pb: 1,
                            color: theme.palette.text.primary,
                            fontWeight: 600,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                        })}
                    >
                        <Box
                            sx={(theme) => ({
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 24,
                                height: 24,
                                backgroundColor: theme.palette.primary.lighter,
                                color: theme.palette.primary.main,
                            })}
                        >
                            <Iconify icon="eva:calendar-outline" width={14} />
                        </Box>
                        {dialogTitle}
                    </DialogTitle>
                    <DialogContent sx={{ pb: 1 }}>
                        <DialogContentText
                            sx={(theme) => ({
                                mb: 2.5,
                                color: theme.palette.text.secondary,
                                fontSize: "0.95rem",
                                lineHeight: 1.5,
                            })}
                        >
                            {message}
                        </DialogContentText>

                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2.5,
                            }}
                        >
                            {!isTabsPos ? (
                                <>
                                    {/* Guest Count Fields */}
                                    <Grid container spacing={2}>
                                        <Grid item xs={4}>
                                            <TextField
                                                label="Adults"
                                                type="number"
                                                fullWidth
                                                variant="outlined"
                                                value={formData.adults}
                                                onChange={(e) =>
                                                    handleNumberChange(
                                                        "adults",
                                                        e.target.value
                                                    )
                                                }
                                                onFocus={(e) => e.target.select()}
                                                onClick={(e) => e.target.select()}
                                                inputProps={{
                                                    min: 1,
                                                    max: 20,
                                                }}
                                                sx={(theme) => ({
                                                    "& .MuiOutlinedInput-root": {
                                                        "&:hover .MuiOutlinedInput-notchedOutline":
                                                            {
                                                                borderColor:
                                                                    theme.palette
                                                                        .primary.main,
                                                            },
                                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                            {
                                                                borderColor:
                                                                    theme.palette
                                                                        .primary.main,
                                                                borderWidth: 2,
                                                            },
                                                    },
                                                })}
                                            />
                                        </Grid>
                                        <Grid item xs={4}>
                                            <TextField
                                                label="Kids"
                                                type="number"
                                                fullWidth
                                                variant="outlined"
                                                value={formData.kids}
                                                onChange={(e) =>
                                                    handleNumberChange(
                                                        "kids",
                                                        e.target.value
                                                    )
                                                }
                                                onFocus={(e) => e.target.select()}
                                                onClick={(e) => e.target.select()}
                                                inputProps={{
                                                    min: 0,
                                                    max: 20,
                                                }}
                                                sx={(theme) => ({
                                                    "& .MuiOutlinedInput-root": {
                                                        "&:hover .MuiOutlinedInput-notchedOutline":
                                                            {
                                                                borderColor:
                                                                    theme.palette
                                                                        .primary.main,
                                                            },
                                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                            {
                                                                borderColor:
                                                                    theme.palette
                                                                        .primary.main,
                                                                borderWidth: 2,
                                                            },
                                                    },
                                                })}
                                            />
                                        </Grid>
                                        <Grid item xs={4}>
                                            <TextField
                                                label="Babies"
                                                type="number"
                                                fullWidth
                                                variant="outlined"
                                                value={formData.babies}
                                                onChange={(e) =>
                                                    handleNumberChange(
                                                        "babies",
                                                        e.target.value
                                                    )
                                                }
                                                onFocus={(e) => e.target.select()}
                                                onClick={(e) => e.target.select()}
                                                inputProps={{
                                                    min: 0,
                                                    max: 20,
                                                }}
                                                sx={(theme) => ({
                                                    "& .MuiOutlinedInput-root": {
                                                        "&:hover .MuiOutlinedInput-notchedOutline":
                                                            {
                                                                borderColor:
                                                                    theme.palette
                                                                        .primary.main,
                                                            },
                                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                            {
                                                                borderColor:
                                                                    theme.palette
                                                                        .primary.main,
                                                                borderWidth: 2,
                                                            },
                                                    },
                                                })}
                                            />
                                        </Grid>
                                    </Grid>
                                </>
                            ) : (
                                <>
                                    <Grid container spacing={2}>
                                        <Grid item xs={formData.use_delivery_provider ? 12 : 6}>
                                            <TextField
                                                autoFocus
                                                select
                                                label="Estimated Preparation Time"
                                                fullWidth
                                                variant="outlined"
                                                value={
                                                    formData.estimated_preparation_time
                                                }
                                                onChange={(e) =>
                                                    handleInputChange(
                                                        "estimated_preparation_time",
                                                        e.target.value
                                                    )
                                                }
                                                sx={(theme) => ({
                                                    "& .MuiOutlinedInput-root": {
                                                        "&:hover .MuiOutlinedInput-notchedOutline":
                                                            {
                                                                borderColor:
                                                                    theme.palette
                                                                        .primary.main,
                                                            },
                                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                            {
                                                                borderColor:
                                                                    theme.palette
                                                                        .primary.main,
                                                                borderWidth: 2,
                                                            },
                                                    },
                                                })}
                                            >
                                                {estimatedPreparationTimeOptions.map(
                                                    (t) => (
                                                        <MenuItem
                                                            key={`prep_${t}`}
                                                            value={t}
                                                        >
                                                            {t}
                                                        </MenuItem>
                                                    )
                                                )}
                                            </TextField>
                                        </Grid>
                                        {!formData.use_delivery_provider ? (
                                            <Grid item xs={6}>
                                                <TextField
                                                    select
                                                    label="Designated Number"
                                                    fullWidth
                                                    variant="outlined"
                                                    value={formData.designated_number}
                                                    onChange={(e) =>
                                                        handleInputChange(
                                                            "designated_number",
                                                            e.target.value
                                                        )
                                                    }
                                                    sx={(theme) => ({
                                                        "& .MuiOutlinedInput-root": {
                                                            "&:hover .MuiOutlinedInput-notchedOutline":
                                                                {
                                                                    borderColor:
                                                                        theme.palette
                                                                            .primary.main,
                                                                },
                                                            "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                                {
                                                                    borderColor:
                                                                        theme.palette
                                                                            .primary.main,
                                                                    borderWidth: 2,
                                                                },
                                                        },
                                                    })}
                                                >
                                                    <MenuItem value="">
                                                        None
                                                    </MenuItem>
                                                    {designatedNumberOptions.map(
                                                        (n) => (
                                                            <MenuItem
                                                                key={`dn_${n}`}
                                                                value={String(n)}
                                                            >
                                                                {n}
                                                            </MenuItem>
                                                        )
                                                    )}
                                                </TextField>
                                            </Grid>
                                        ) : null}
                                        <Grid item xs={12} sx={{ pt: "4px !important" }}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={!!formData.use_delivery_provider}
                                                        onChange={(e) => {
                                                            const checked = e.target.checked;
                                                            const existingCustomerText =
                                                                String(formData.customer || "").trim();
                                                            setFormData((prev) => ({
                                                                ...prev,
                                                                use_delivery_provider: checked,
                                                                delivery_provider: checked
                                                                    ? prev.delivery_provider
                                                                    : "",
                                                                // Designated number is only for non-delivery flows
                                                                designated_number: checked
                                                                    ? ""
                                                                    : prev.designated_number,
                                                            }));
                                                            if (checked) {
                                                                // Carry over existing plain-text customer into the typeahead input
                                                                if (existingCustomerText) {
                                                                    setCustomerInputValue(existingCustomerText);
                                                                }
                                                                // If we already have a provider value (typed/selected), reflect it in the input UI
                                                                const providerText = String(
                                                                    formData.delivery_provider || ""
                                                                ).trim();
                                                                if (providerText) {
                                                                    const match = deliveryProviders.find(
                                                                        (p) => p?.name === providerText
                                                                    );
                                                                    setDeliveryProviderInputValue(
                                                                        match?.company || providerText
                                                                    );
                                                                }
                                                            }
                                                        }}
                                                    />
                                                }
                                                label="Use delivery provider"
                                            />
                                        </Grid>

                                        {formData.use_delivery_provider ? (
                                            <>
                                                <Grid item xs={12}>
                                                    <Autocomplete
                                                        freeSolo
                                                        loading={deliveryProvidersLoading}
                                                        options={deliveryProviders}
                                                        getOptionLabel={(opt) => {
                                                            if (typeof opt === "string") return opt;
                                                            return opt?.company || opt?.name || "";
                                                        }}
                                                        inputValue={deliveryProviderInputValue}
                                                        onInputChange={(_, value) => {
                                                            const v = value || "";
                                                            setDeliveryProviderInputValue(v);
                                                            // Store typed value; backend will resolve by company (or create) and return docname
                                                            handleInputChange("delivery_provider", v);
                                                        }}
                                                        onChange={(_, newValue) => {
                                                            if (!newValue) return;
                                                            if (typeof newValue === "string") {
                                                                handleInputChange("delivery_provider", newValue);
                                                                return;
                                                            }
                                                            // Store docname for link field, but show company as label
                                                            handleInputChange("delivery_provider", newValue.name);
                                                            setDeliveryProviderInputValue(
                                                                newValue.company || newValue.name
                                                            );
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label="Delivery Provider"
                                                                fullWidth
                                                                variant="outlined"
                                                            />
                                                        )}
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Autocomplete
                                                        freeSolo
                                                        loading={customerLoading}
                                                        options={customerOptions}
                                                        getOptionLabel={(opt) => {
                                                            if (typeof opt === "string") return opt;
                                                            return opt?.customer_name || opt?.name || "";
                                                        }}
                                                        inputValue={customerInputValue}
                                                        onInputChange={(_, value) => {
                                                            setCustomerInputValue(value || "");
                                                            handleInputChange("customer", value || "");
                                                        }}
                                                        onChange={(_, newValue) => {
                                                            if (!newValue) return;
                                                            if (typeof newValue === "string") {
                                                                handleInputChange("customer", newValue);
                                                                return;
                                                            }
                                                            // existing customer selected -> store docname for backend
                                                            handleInputChange("customer", newValue.name);
                                                            handleInputChange("mobile_no", newValue.mobile_no || "");
                                                            const parsed = parsePrimaryAddressLines(
                                                                newValue.primary_address
                                                            );
                                                            if (parsed.line1) {
                                                                handleInputChange("address_line1", parsed.line1);
                                                            }
                                                            if (parsed.line2) {
                                                                handleInputChange("address_line2", parsed.line2);
                                                            }
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label="Customer"
                                                                fullWidth
                                                                variant="outlined"
                                                            />
                                                        )}
                                                    />
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <TextField
                                                        label="Mobile number"
                                                        fullWidth
                                                        variant="outlined"
                                                        value={formData.mobile_no}
                                                        onChange={(e) =>
                                                            handleInputChange(
                                                                "mobile_no",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <TextField
                                                        label="Address line 1"
                                                        fullWidth
                                                        variant="outlined"
                                                        value={formData.address_line1}
                                                        onChange={(e) =>
                                                            handleInputChange(
                                                                "address_line1",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <TextField
                                                        label="Address line 2"
                                                        fullWidth
                                                        variant="outlined"
                                                        value={formData.address_line2}
                                                        onChange={(e) =>
                                                            handleInputChange(
                                                                "address_line2",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </Grid>
                                            </>
                                        ) : (
                                            <>
                                                <Grid item xs={12}>
                                                    <TextField
                                                        label="Customer"
                                                        fullWidth
                                                        variant="outlined"
                                                        value={formData.customer}
                                                        onChange={(e) =>
                                                            handleInputChange(
                                                                "customer",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </Grid>
                                            </>
                                        )}
                                    </Grid>
                                </>
                            )}

                            {/* Notes Field */}
                            <TextField
                                autoFocus={!isTabsPos}
                                label="Notes"
                                fullWidth
                                variant="outlined"
                                value={formData.notes}
                                onChange={(e) =>
                                    handleInputChange("notes", e.target.value)
                                }
                                onKeyDown={handleKeyDown}
                                placeholder="Enter any special notes or requirements..."
                                multiline
                                rows={3}
                                helperText="Press Ctrl+Enter to submit quickly"
                                sx={(theme) => ({
                                    "& .MuiOutlinedInput-root": {
                                        "&:hover .MuiOutlinedInput-notchedOutline":
                                            {
                                                borderColor:
                                                    theme.palette.primary.main,
                                            },
                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                            {
                                                borderColor:
                                                    theme.palette.primary.main,
                                                borderWidth: 2,
                                            },
                                    },
                                    "& .MuiFormHelperText-root": {
                                        color: theme.palette.text.disabled,
                                        fontSize: "0.8rem",
                                    },
                                })}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5 }}>
                        <Button
                            onClick={handleCancel}
                            variant="outlined"
                            sx={(theme) => ({
                                borderColor: theme.palette.grey[300],
                                color: theme.palette.grey[600],
                                "&:hover": {
                                    borderColor: theme.palette.grey[400],
                                    backgroundColor: theme.palette.grey[50],
                                },
                            })}
                        >
                            {cancelText}
                        </Button>
                        <Button
                            onClick={handleConfirm}
                            variant="contained"
                            color="primary"
                            sx={(theme) => ({
                                minWidth: 100,
                                boxShadow:
                                    theme.customShadows?.primary ||
                                    theme.shadows[8],
                                "&:hover": {
                                    boxShadow:
                                        theme.customShadows?.primary ||
                                        theme.shadows[12],
                                },
                            })}
                        >
                            {confirmText}
                        </Button>
                    </DialogActions>
                </Dialog>
            </ThemeProvider>
        </StyledEngineProvider>
    );
};

export const showReservationPrompt = (options = {}) => {
    return new Promise((resolve, reject) => {
        // Create a container for the dialog
        const container = document.createElement("div");
        document.body.appendChild(container);

        const root = ReactDOM.createRoot(container);

        const cleanup = () => {
            // Cleanup after dialog closes
            setTimeout(() => {
                root.unmount();
                document.body.removeChild(container);
            }, 300); // Small delay to allow dialog close animation
        };

        const handleResolve = (formData) => {
            cleanup();
            resolve(formData);
        };

        const handleReject = (error) => {
            cleanup();
            reject(error);
        };

        root.render(
            <ReservationPromptDialog
                onResolve={handleResolve}
                onReject={handleReject}
                options={options}
            />
        );
    });
};

export default ReservationPromptDialog;
